<template>
  <div class="outer-box max-w-xl">
    <h4>Twilio LC Number Migration</h4>

    <b-alert show variant="warning" class="text-left m-3">
      <b>Important:</b> This tool handles Twilio ISV migration data. You can either enter individual migration details or upload a CSV file with multiple migrations.
    </b-alert>

    <div class="inner-box" style="text-align: left; width: 500px;">

      <!-- Manual Form Section -->
      <div class="mb-4">
        <h5>Manual Entry</h5>
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label>Losing Account SID *</label>
              <input
                class="hl_input--wrap"
                type="text"
                placeholder="Losing Account SID"
                v-model="formData.oldAccountSid"
              />
            </div>
            <div class="mb-3">
              <label>Address SID (Optional)</label>
              <input
                class="hl_input--wrap"
                type="text"
                placeholder="Address SID"
                v-model="formData.addressSid"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label>Gaining Account SID *</label>
              <input
                class="hl_input--wrap"
                type="text"
                placeholder="Gaining Account SID"
                v-model="formData.newAccountSid"
              />
            </div>
            <div class="mb-3">
              <label>Bundle SID (Optional)</label>
              <input
                class="hl_input--wrap"
                type="text"
                placeholder="Bundle SID"
                v-model="formData.bundleSid"
              />
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label>Numbers (comma separated)</label>
          <textarea
            class="hl_input--wrap"
            placeholder="Enter phone numbers separated by commas (e.g., +1234567890, +0987654321)"
            v-model="numbersInput"
            rows="3"
          ></textarea>
        </div>

        <button
          class="btn btn-primary"
          @click="submitManualData"
          :disabled="!isManualFormValid || processing"
        >
          <moon-loader v-if="processing && !isFileUpload" :loading="true" size="21px" color="#fafafa" />
          <span v-else>Submit Migration Data</span>
        </button>
      </div>

      <hr />

      <!-- CSV Upload Section -->
      <div class="mb-4">
        <h5>CSV Upload</h5>
        <b-alert show variant="info" class="text-left">
          Upload a CSV file with columns: losingAccountSid, gainingAccountSid, numbers <b>(Just one number per row)</b>, addressSid (optional), bundleSid (optional)
        </b-alert>

        <input
          ref="csvFileInput"
          class="hl_input--wrap mb-3"
          type="file"
          @change="handleFileUpload"
          accept=".csv"
        />

        <button
          class="btn btn-success"
          @click="uploadCsvData"
          :disabled="!selectedFile || processing"
        >
          <moon-loader v-if="processing && isFileUpload" :loading="true" size="21px" color="#fafafa" />
          <span v-else>Upload CSV</span>
        </button>
      </div>

      <!-- Response Display -->
      <pre v-if="response" style="background-color: #f2f7fa; padding: 16px; margin-top: 16px; white-space: pre-wrap;">{{response}}</pre>
    </div>
  </div>
</template>

<script>
import { ISVService } from "@/services";
import store from "@/store";

export default {
  data() {
    return {
      formData: {
        locationId: '',
        companyId: '',
        oldAccountSid: '',
        newAccountSid: '',
        addressSid: '',
        bundleSid: ''
      },
      numbersInput: '',
      selectedFile: null,
      processing: false,
      isFileUpload: false,
      response: null
    };
  },
  computed: {
    isManualFormValid() {
      return this.formData.oldAccountSid &&
             this.formData.newAccountSid;
    }
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.response = null;
      } else {
        this.selectedFile = null;
      }
    },

    async submitManualData() {
      if (!this.isManualFormValid) {
        this.response = { message: "Please fill in all required fields (Losing Account SID, Gaining Account SID)" };
        return;
      }

      this.processing = true;
      this.isFileUpload = false;
      this.response = null;

      try {
        // Prepare data object
        const migrationData = {
          ...this.formData
        };

        // Parse numbers if provided
        if (this.numbersInput.trim()) {
          migrationData.numbers = this.numbersInput.split(',').map(num => num.trim()).filter(num => num);
        }

        // Remove empty optional fields
        if (!migrationData.addressSid) delete migrationData.addressSid;
        if (!migrationData.bundleSid) delete migrationData.bundleSid;

        const response = await ISVService.handleTwilioISVMigration(migrationData);
        this.response = response.data;
        this.resetForm();
      } catch (error) {
        if (error.response && error.response.data) {
          this.response = error.response.data;
        } else {
          this.response = {
            message: "An error occurred during migration data submission.",
            error: error.message
          };
        }
      } finally {
        this.processing = false;
      }
    },

    async uploadCsvData() {
      if (!this.selectedFile) {
        this.response = { message: "Please select a CSV file first." };
        return;
      }

      this.processing = true;
      this.isFileUpload = true;
      this.response = null;

      try {
        // Create FormData object
        const formData = new FormData();
        formData.append('csvFile', this.selectedFile, this.selectedFile.name);
        const user = store.state.auth.user;
        const userEmail = user.email;

        const response = await ISVService.handleTwilioISVMigrationWithFile(formData, userEmail);
        this.response = response.data;
        this.resetFileUpload();
      } catch (error) {
        if (error.response && error.response.data) {
          this.response = error.response.data;
        } else {
          this.response = {
            message: "An error occurred during CSV upload.",
            error: error.message
          };
        }
      } finally {
        this.processing = false;
      }
    },

    resetForm() {
      this.formData = {
        oldAccountSid: '',
        newAccountSid: '',
        addressSid: '',
        bundleSid: ''
      };
      this.numbersInput = '';
    },

    resetFileUpload() {
      if (this.$refs.csvFileInput) {
        this.$refs.csvFileInput.value = null;
      }
      this.selectedFile = null;
    }
  }
};
</script>

<style scoped>
label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.row {
  margin-left: -15px;
  margin-right: -15px;
}

.col-md-6 {
  padding-left: 15px;
  padding-right: 15px;
  width: 50%;
  float: left;
}

hr {
  border: 0;
  height: 1px;
  background: #ccc;
  margin: 20px 0;
}

h5 {
  color: #333;
  margin-bottom: 15px;
}
</style>
